import React from "react";
import { Box } from "@mui/material";
import {
  AttachFileOutlined,
  DescriptionOutlined,
  StickyNote2Outlined,
} from "@mui/icons-material";
import { dt } from "../../utils/dt";
import { ServiceReport } from "../../models/ServiceReportsModel";
import { ImagePreviewStrip } from "../../../../shared/components/gallery";
import type { GalleryImage } from "../../../../shared/components/gallery";
import ReportFile from "../../../../shared/ReportFile";
import useTranslation from "../../../../shared/hooks/useTranslation";

// ===================================================================
//  COMPONENT
// ===================================================================
interface SrProblemCardProps {
  serviceReport: ServiceReport;
  reportImageFiles: GalleryImage[];
  reportNonImageFiles: Array<any>;
  isPhone: boolean;
}

const SrProblemCard = ({
  serviceReport,
  reportImageFiles,
  reportNonImageFiles,
  isPhone,
}: SrProblemCardProps) => {
  const { translate } = useTranslation("myMachinery");

  const hasAttachments =
    reportImageFiles.length > 0 || reportNonImageFiles.length > 0;

  return (
    <Box
      sx={{
        background: dt.machinery.card.bg,
        border: dt.machinery.card.border,
        borderRadius: dt.machinery.card.radius,
        boxShadow: dt.machinery.card.shadow,
        padding: isPhone ? "16px" : "24px",
        fontFamily: dt.font,
        overflow: "hidden",
      }}
    >
      {/* Title as hero text */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: isPhone ? "17px" : "20px",
          fontWeight: 700,
          color: dt.textPrimary,
          lineHeight: 1.35,
          mb: 2,
        }}
      >
        {serviceReport.title}
      </Box>

      {/* Problem Description — always visible, full text */}
      <Box sx={{ pt: 2, borderTop: `1px solid ${dt.cardBorder}` }}>
        <SectionLabel
          label={translate("srDetail.problemDescription")}
          accentColor={dt.serviceReports.detail.problemAccent}
          icon={<DescriptionOutlined sx={{ fontSize: 14 }} />}
        />
        {serviceReport.description ? (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "14px",
              color: dt.textSecondary,
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {serviceReport.description}
          </Box>
        ) : (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "14px",
              color: dt.textMuted,
              fontStyle: "italic",
            }}
          >
            {translate("srDetail.noDescription")}
          </Box>
        )}
      </Box>

      {/* Technical Notes — always visible, full text */}
      {serviceReport.notes && (
        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: `1px solid ${dt.cardBorder}`,
          }}
        >
          <SectionLabel
            label={translate("srDetail.technicalNotes")}
            accentColor={dt.serviceReports.detail.problemAccent}
            icon={<StickyNote2Outlined sx={{ fontSize: 14 }} />}
          />
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "14px",
              color: dt.textSecondary,
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {serviceReport.notes}
          </Box>
        </Box>
      )}

      {/* Attachments — always visible */}
      {hasAttachments && (
        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: `1px solid ${dt.cardBorder}`,
          }}
        >
          <SectionLabel
            label={translate("srDetail.reportAttachments")}
            accentColor={dt.serviceReports.detail.problemAccent}
            icon={<AttachFileOutlined sx={{ fontSize: 14 }} />}
          />
          {reportImageFiles.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <ImagePreviewStrip
                images={reportImageFiles}
                maxVisible={4}
                thumbnailSize={isPhone ? 48 : 64}
              />
            </Box>
          )}
          {reportNonImageFiles.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: 1,
              }}
            >
              {reportNonImageFiles.map((file: any, i: number) => (
                <ReportFile
                  key={i}
                  file={file}
                  canDelete={false}
                  canDownload={true}
                  fileIndex={i}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// ===================================================================
//  SECTION LABEL (reused across problem and solution cards)
// ===================================================================
export const SectionLabel = ({
  label,
  accentColor,
  icon,
}: {
  label: string;
  accentColor: string;
  icon?: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontFamily: dt.font,
      fontSize: "11px",
      fontWeight: 700,
      color: accentColor,
      textTransform: "uppercase",
      letterSpacing: "0.8px",
      mb: 1.25,
    }}
  >
    {icon}
    {label}
  </Box>
);

export default SrProblemCard;
